/**
 * A template holds kinds of objects, not real ones — so the authoring canvas draws CLASSIFICATIONS, and every
 * mark on it is a template object: a kind of thing, and what the object it places will be called.
 *
 * Everything here is pure, because it is the part that has to be right: the band an object sits in,
 * whether an edge is legal, what the generated IDs will read like, and the round trip between the
 * canvas and the Objects/Edges the API stores. The editor holds objects + edges in state and re-draws
 * the layout from scratch on every render — a template has no saved geometry, and the instantiate
 * wizard lays objects out by band anyway, so there is nothing to persist and nothing to go stale.
 *
 * Legality is `ClassificationRules`, the same whitelist the planning canvas and the server use
 * (backend/src/routes/api/templates.js validateStructure). Checking it here as the user draws is
 * not a second opinion — it is the same rule, applied before the 422 instead of after it.
 */
import type { Classification, ClassificationRule } from '@/features/classifications/api'
import type { Template, TemplateEdge, TemplateObjectInput } from '../api'

/**
 * An object as the editor holds it: the API's shape, with the flags as booleans.
 *
 * Every template object is exactly one object. It used to be able to stand for a number of objects
 * chosen when the template was used, numbered by {n}, with a pairing rule on the dependencies
 * between two such objects. Andrew, 2026-09-22: "We don't have a number of objects, remove it."
 * Two of something are two objects on the canvas, which offers another object of any class
 * already drawn (isOfferable).
 */
export interface ObjectDraft {
    ObjectKey: string
    ClassificationID: number
    NamePattern: string
    DefaultDescription?: string | null
    AttachDown: boolean
    AttachUp: boolean
    MasterRunbookID?: number | null
}

export interface EdgeDraft {
    DependentObject: string
    ProviderObject: string
}

/** The name shown in the instantiate wizard's preview, and in the object card. */
export const SAMPLE_PREFIX = 'ACME'

/** Bands are 120px apart and objects 210px, which is what the planning canvas uses at Card size. */
const LANE_H = 120
const OBJECT_W = 210
const BAND_PAD_X = 60

/**
 * How the objects are spread out. The canvas offers these the way the planning canvas offers its
 * own gaps and node modes: a template's geometry is redrawn from the bands on every render, so
 * changing one costs nothing and is never saved.
 *
 * `objectWidth` follows the node mode — a graphic is a square of artwork, a card is a labelled
 * strip — and it decides the spacing as much as the gap does, so the two travel together.
 */
export interface LayoutOptions {
    objectWidth?: number
    laneHeight?: number
    gapX?: number
}

/** Card mode's node is as wide as the card; graphic mode's is the artwork's box. */
export const OBJECT_WIDTH = { Card: OBJECT_W, Graphic: 96 } as const

/**
 * Room kept clear at the top of every lane for the band's heading.
 *
 * BandRowNode draws its heading at top:6, left:10 inside the strip — "VIRTUAL MACHINE  Compute"
 * runs well past x=0 — and objects used to be placed only 8px below the strip's top edge, so the
 * first object in each lane sat on the heading and hid it (Andrew, 2026-09-21). The heading is how a
 * lane says which classification it is; covering it hides the one fact the layout exists to show.
 */
export const BAND_HEADING_H = 22

/** The shortest lane that fits a node of this height below its heading, with air underneath. */
export function minLaneHeight(nodeHeight: number): number {
    return BAND_HEADING_H + nodeHeight + 28
}

/**
 * An object key is what the edges reference and what the wizard shows, so it has to be readable and
 * stable: the classification's own letters, uppercased, and a number only when a second object of
 * the same class needs one (ESXIHOST, ESXIHOST2).
 */
export function objectKeyFor(className: string, taken: Iterable<string>): string {
    const base = String(className || 'OBJECT')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 10) || 'OBJECT'
    const used = new Set(taken)
    if (!used.has(base)) return base
    for (let n = 2; n < 100; n += 1) {
        if (!used.has(`${base}${n}`)) return `${base}${n}`
    }
    return `${base}${Date.now()}`
}

/**
 * `{prefix}` is filled in at instantiate time, and the server requires it, so it is built in rather
 * than left to the user to remember.
 */
export function namePatternFor(objectKey: string): string {
    return `{prefix}-${objectKey}`
}

/** What this template object's object will be called. Mirrors the server's instanceName. */
export function previewName(object: ObjectDraft, prefix = SAMPLE_PREFIX): string {
    return String(object.NamePattern || '').replace(/\{prefix\}/g, prefix)
}

/** The rule whitelist as a set, keyed the way the server keys it. */
export function ruleSet(rules: ClassificationRule[] | undefined): Set<string> {
    return new Set((rules || []).map((r) => `${Number(r.DependentClassID)}|${Number(r.ProviderClassID)}`))
}

/** May something of `dependentClassId` depend on something of `providerClassId`? */
export function canDepend(rules: Set<string>, dependentClassId: number, providerClassId: number): boolean {
    return rules.has(`${Number(dependentClassId)}|${Number(providerClassId)}`)
}

/**
 * Is this classification worth offering? An empty canvas can start anywhere; after that, a class
 * earns its place in the palette only if it could legally sit above or below something already
 * drawn. Greyed rather than hidden: "why can I not use this one" is a fair question, and the
 * answer is the rules, not the palette.
 *
 * A class already on the canvas is always offered. Another object of it can attach wherever the
 * first can, and a template holds several objects of one class by design. The rules alone greyed
 * it out whenever nothing it could attach to was drawn yet: one Hypervisor on the canvas, and a
 * second could not be added until something else was (Andrew, 2026-09-22), because no rule lets a
 * Hypervisor depend on a Hypervisor.
 */
export function isOfferable(rules: Set<string>, drawnClassIds: number[], classId: number): boolean {
    if (!drawnClassIds.length) return true
    if (drawnClassIds.includes(Number(classId))) return true
    return drawnClassIds.some(
        (drawn) => canDepend(rules, drawn, classId) || canDepend(rules, classId, drawn),
    )
}

/**
 * Where each object sits: one lane per band present, ordered by DisplayRank with the highest at the
 * top, exactly as the planning canvas draws them. x spreads objects along their lane in object order.
 */
export function laneLayout(
    objects: ObjectDraft[],
    classById: Map<number, Classification>,
    { objectWidth = OBJECT_W, laneHeight = LANE_H, gapX = 40 }: LayoutOptions = {},
) {
    const ranks = [...new Set(objects.map((r) => Number(classById.get(r.ClassificationID)?.DisplayRank ?? 0)))]
        .sort((a, b) => a - b)
    const rowOf = new Map(ranks.map((rank, i) => [rank, i]))
    const maxRow = Math.max(0, ranks.length - 1)
    const perRow = new Map<number, number>()
    const positions = new Map<string, { x: number; y: number }>()
    const step = objectWidth + gapX
    for (const object of objects) {
        const rank = Number(classById.get(object.ClassificationID)?.DisplayRank ?? 0)
        const row = rowOf.get(rank) ?? 0
        const index = perRow.get(row) ?? 0
        perRow.set(row, index + 1)
        positions.set(object.ObjectKey, { x: index * step, y: (maxRow - row) * laneHeight + BAND_HEADING_H })
    }
    const widest = Math.max(1, ...[...perRow.values()])
    return { positions, ranks, rowOf, maxRow, laneHeight, laneWidth: widest * step + BAND_PAD_X }
}

/** The band strips behind the objects — the same node type the planning canvas uses. */
export function bandNodes(
    objects: ObjectDraft[],
    classById: Map<number, Classification>,
    bandLabel: (rank: number) => string,
    layout: LayoutOptions = {},
) {
    const { ranks, rowOf, maxRow, laneWidth, laneHeight } = laneLayout(objects, classById, layout)
    const classAtRank = new Map<number, Classification>()
    for (const object of objects) {
        const cls = classById.get(object.ClassificationID)
        if (cls && !classAtRank.has(Number(cls.DisplayRank))) classAtRank.set(Number(cls.DisplayRank), cls)
    }
    return ranks.map((rank) => ({
        id: `band-${rank}`,
        type: 'bandRow',
        draggable: false,
        selectable: false,
        focusable: false,
        zIndex: -1,
        style: { pointerEvents: 'none' as const },
        position: { x: -BAND_PAD_X, y: (maxRow - (rowOf.get(rank) ?? 0)) * laneHeight - 8 },
        data: {
            label: classAtRank.get(rank)?.Name || bandLabel(rank),
            sublabel: classAtRank.get(rank)?.Category || '',
            color: classAtRank.get(rank)?.BackgroundColor || undefined,
            width: laneWidth + BAND_PAD_X * 2,
            height: laneHeight - 12,
        },
    }))
}

/** Objects as canvas nodes, drawn on top of the bands. */
export function objectNodes(
    objects: ObjectDraft[],
    classById: Map<number, Classification>,
    layout: LayoutOptions = {},
) {
    const { positions } = laneLayout(objects, classById, layout)
    return objects.map((object) => ({
        id: object.ObjectKey,
        type: 'templateObject',
        position: positions.get(object.ObjectKey) || { x: 0, y: 0 },
        // The lane IS the classification, so dragging an object off its band would say something
        // untrue. Objects are positioned, never dragged.
        draggable: false,
        data: { object, classification: classById.get(object.ClassificationID) || null },
    }))
}

/**
 * Edges, in the direction the rest of the canvas draws them: the PROVIDER is the source and the
 * DEPENDENT is the target, so a connection is dragged upward from the thing underneath to the
 * thing that needs it. That is also how MainMapping stores it (SourceID provides, TargetID
 * depends), and getting it backwards is the oldest bug in this model.
 */
export function graphEdges(edges: EdgeDraft[]) {
    return edges.map((e) => ({
        id: edgeId(e.DependentObject, e.ProviderObject),
        source: e.ProviderObject,
        target: e.DependentObject,
    }))
}

export const edgeId = (dependentObject: string, providerObject: string) => `${dependentObject}<-${providerObject}`

/** A connection the user just drew, as a draft edge. Source provides, target depends. */
export function edgeFromConnection(source: string, target: string): EdgeDraft {
    return { DependentObject: target, ProviderObject: source }
}

/** What the API stores. Sort order follows the band, top of the stack first. */
export function toSaveInput(objects: ObjectDraft[], edges: EdgeDraft[], classById: Map<number, Classification>): {
    Objects: TemplateObjectInput[]
    Edges: TemplateEdge[]
} {
    const rankOf = (r: ObjectDraft) => Number(classById.get(r.ClassificationID)?.DisplayRank ?? 0)
    const ordered = [...objects].sort((a, b) => rankOf(b) - rankOf(a) || a.ObjectKey.localeCompare(b.ObjectKey))
    return {
        Objects: ordered.map((r, i) => ({
            ObjectKey: r.ObjectKey,
            ClassificationID: Number(r.ClassificationID),
            NamePattern: r.NamePattern,
            DefaultDescription: r.DefaultDescription || null,
            AttachDown: r.AttachDown ? 1 : 0,
            AttachUp: r.AttachUp ? 1 : 0,
            SortOrder: i + 1,
            MasterRunbookID: r.MasterRunbookID ?? null,
        })),
        Edges: edges.map((e) => ({ DependentObject: e.DependentObject, ProviderObject: e.ProviderObject })),
    }
}

/** An existing template, opened for editing. */
export function fromTemplate(tpl: Template): { objects: ObjectDraft[]; edges: EdgeDraft[] } {
    return {
        objects: (tpl.objects || []).map((r) => ({
            ObjectKey: r.ObjectKey,
            ClassificationID: Number(r.ClassificationID),
            NamePattern: r.NamePattern,
            DefaultDescription: r.DefaultDescription ?? null,
            AttachDown: !!Number(r.AttachDown),
            AttachUp: !!Number(r.AttachUp),
            MasterRunbookID: (r as { MasterRunbookID?: number | null }).MasterRunbookID ?? null,
        })),
        edges: (tpl.edges || []).map((e) => ({
            DependentObject: e.DependentObject,
            ProviderObject: e.ProviderObject,
        })),
    }
}

/**
 * The same checks the server makes, so the canvas can say what is wrong while it is still being
 * drawn. Returned as translation keys with their English, because the editor shows them inline.
 */
export function validateDraft(
    objects: ObjectDraft[],
    edges: EdgeDraft[],
    rules: Set<string>,
    classById: Map<number, Classification>,
): { key: string; english: string; detail?: string }[] {
    const problems: { key: string; english: string; detail?: string }[] = []
    if (!objects.length) {
        problems.push({ key: 'templates.editor.errNoObjects', english: 'A template needs at least one object.' })
    }
    const seen = new Set<string>()
    for (const r of objects) {
        const key = String(r.ObjectKey || '').trim()
        if (!key) problems.push({ key: 'templates.editor.errNoKey', english: 'Every object needs a key.' })
        else if (seen.has(key)) {
            problems.push({ key: 'templates.editor.errDuplicateKey', english: 'Two objects share the key {{key}}.', detail: key })
        }
        seen.add(key)
        if (!String(r.NamePattern || '').includes('{prefix}')) {
            problems.push({ key: 'templates.editor.errNoPrefix', english: '{{key}} must include {prefix} in its name pattern.', detail: key })
        }
        if (String(r.NamePattern || '').includes('{n}')) {
            problems.push({ key: 'templates.editor.errHasN', english: '{{key}} is one object, so its name pattern has no {n} to number it.', detail: key })
        }
        if (!classById.has(Number(r.ClassificationID))) {
            problems.push({ key: 'templates.editor.errRetiredClass', english: "{{key}}'s classification is no longer available — give it a new one.", detail: key })
        }
    }
    const classOf = new Map(objects.map((r) => [r.ObjectKey, Number(r.ClassificationID)]))
    for (const e of edges) {
        const dep = classOf.get(e.DependentObject)
        const prov = classOf.get(e.ProviderObject)
        if (dep == null || prov == null) continue
        if (!canDepend(rules, dep, prov)) {
            problems.push({
                key: 'templates.editor.errIllegalEdge',
                english: '{{key}} is not allowed by the classification rules.',
                detail: `${e.DependentObject} → ${e.ProviderObject}`,
            })
        }
    }
    return problems
}
